// Partners Manager Component
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { PartnerForm } from './AdminShared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/partners/all`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPartners(data || []);
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce partenaire ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/partners/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchPartners();
    } catch (err) {
      console.error('Error deleting partner:', err);
    }
  };

  const handleSave = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/partners/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setEditingId(null);
      fetchPartners();
    } catch (err) {
      console.error('Error saving partner:', err);
    }
  };

  const handleAdd = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/partners`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setShowAddForm(false);
      setEditForm({});
      fetchPartners();
    } catch (err) {
      console.error('Error adding partner:', err);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-slate-800">Partenaires</h2>
        <Button onClick={() => {
          setShowAddForm(true);
          setEditForm({ name: '', country: '', logo: '', website: '', description_en: '', description_fr: '', description_ar: '', full_description_en: '', full_description_fr: '', full_description_ar: '', images: [], video_id: '', order: partners.length, is_active: true });
        }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {showAddForm && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-800 mb-4">Nouveau Partenaire</h3>
          <PartnerForm form={editForm} setForm={setEditForm} onSave={handleAdd} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      <div className="space-y-4">
        {partners.map((partner) => (
          <div key={partner.id} className="border border-slate-200 rounded-lg p-4">
            {editingId === partner.id ? (
              <PartnerForm form={editForm} setForm={setEditForm} onSave={() => handleSave(partner.id)} onCancel={() => setEditingId(null)} />
            ) : (
              <div className="flex items-center gap-4">
                <img src={partner.logo} alt={partner.name} className="w-20 h-12 object-contain bg-white rounded" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-slate-800">{partner.name}</h3>
                    <span className="text-sm text-slate-500">({partner.country})</span>
                    {!partner.is_active && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Inactif</span>}
                  </div>
                  <p className="text-sm text-slate-500 truncate">{partner.description_fr}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingId(partner.id); setEditForm(partner); }}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(partner.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {partners.length === 0 && !showAddForm && <div className="text-center py-12 text-slate-500">Aucun partenaire.</div>}
    </div>
  );
};

export default PartnersManager;
